import React, { useEffect, useRef, useState } from "react";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  /** vertical translate amount in px (default 24) */
  y?: number;
  /** disable initial translate, just fade */
  fadeOnly?: boolean;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Lightweight scroll-reveal wrapper. Subtle fade + rise once the element
 * enters the viewport. Respects `prefers-reduced-motion`.
 */
const Reveal: React.FC<RevealProps> = ({
  children,
  className = "",
  delay = 0,
  y = 32,
  fadeOnly = false,
  as: As = "div",
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const translate = fadeOnly ? 0 : y;

  const Component = As as any;
  return (
    <Component
      ref={ref as any}
      style={{
        transitionDelay: `${delay}ms`,
        transform: visible ? "none" : `translate3d(0, ${translate}px, 0)`,
        opacity: visible ? 1 : 0,
        transitionProperty: "opacity, transform",
        transitionDuration: "1400ms",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        willChange: "opacity, transform",
      }}
      className={className}
    >
      {children}
    </Component>
  );
};

export default Reveal;
